import getSuggestions from '../actions/getSuggestions';
import initializeLazyScenarioStore from '../actions/initializeLazyScenarioStore';
import onGhostTextAccepted from '../actions/onGhostTextAccepted';
import onPillSuggestionSelected from '../actions/onPillSuggestionSelected';
import onSearchInputClicked from '../actions/onSearchInputClicked';
import onSearchInputFocused from '../actions/onSearchInputFocused';
import onSearchTextChanged from '../actions/onSearchTextChanged';
import { SUGGESTION_ID_PREFIX, SUGGESTIONS_CALLOUT_ID } from '../constants';
import getSuggestionAtIndex from '../selectors/getSuggestionAtIndex';
import isSearchSessionActive from '../selectors/isSearchSessionActive';
import getScenarioStore from '../store/getScenarioStore';
import getPlaceholderAndAriaLabel from '../utils/getPlaceholderAndAriaLabel';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import { SearchScenarioId } from 'owa-search-next-common';
import { PeopleSuggestion, SuggestionKind } from 'owa-search-service';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import onKeyDownSearchInput, {
    onBackspacePressedSearchInput,
    onDownArrowPressedSearchInput,
    onEnterPressedSearchInput,
    onEscapePressedSearchInput,
    onLeftArrowPressedSearchInput,
    onUpArrowPressedSearchInput,
} from '../actions/onKeyDownSearchInput';

const classNames = require('classnames');
const styles = require('./SearchInput.scss');

export interface SearchInputProps {
    searchPlaceHolderText?: string;
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    scenarioId: SearchScenarioId;
    isSuggestionsCalloutVisible: boolean;
    isUsing3S: boolean;
}

export interface ISearchInput {
    setFocus(): void;
    isGhostTextVisible: boolean;
    searchInputText: string;
    activeDescendant: string;
}

@observer
export default class SearchInput extends React.Component<SearchInputProps, {}>
    implements ISearchInput {
    private searchInput: HTMLInputElement;

    /**
     * Sets focus on the search input. This is exposed publicly so parent component
     * (SearchBox) can set focus on the input element as a result of clicks on other
     * parts of the search box.
     */
    public setFocus() {
        if (this.searchInput) {
            this.searchInput.focus();
        }
    }

    /**
     * This computed handles all of the conditions for determining if ghost text
     * is visible.
     *
     * - There is ghost text in the store.
     * - The suggestions dropdown is visible.
     * - The cursor is at the end of the user-typed input.
     * - User isn't using IE/Edge.
     */
    @computed
    get isGhostTextVisible(): boolean {
        /**
         * IE and old versions of Edge have unexplained behavior when the user
         * goes from a state of having ghost text to not having ghost text.
         *
         * DevOps 44058: Fix search autocomplete for IE/Edge
         */
        if (isBrowserIE() || isBrowserEdge()) {
            return false;
        }

        const { ghostText, isSuggestionsDropdownVisible, searchText } = getScenarioStore(
            this.props.scenarioId
        );

        return (
            ghostText &&
            isSuggestionsDropdownVisible &&
            this.searchInput.selectionStart === searchText.length
        );
    }

    /**
     * This computed returns the text to be displayed in the actual input
     * element.
     */
    @computed
    get searchInputText(): string {
        const { searchText, ghostText } = getScenarioStore(this.props.scenarioId);
        return this.isGhostTextVisible ? `${searchText}${ghostText}` : searchText;
    }

    @computed
    get activeDescendant(): string {
        const { isSuggestionsCalloutVisible, scenarioId } = this.props;
        const { selectedSuggestionIndex } = getScenarioStore(scenarioId);

        /**
         * If search suggestions are visible and the user has a suggestion
         * selected, then return a string to be used for the component's
         * aria-activedescendant attribute.
         */
        if (isSuggestionsCalloutVisible && selectedSuggestionIndex >= 0) {
            return `${SUGGESTION_ID_PREFIX}${selectedSuggestionIndex}`;
        }

        return '';
    }

    /**
     * Highlight ghost text part of input text when component updates.
     */
    componentDidUpdate() {
        const { searchText, ghostText } = getScenarioStore(this.props.scenarioId);

        const startHighlightIndex = searchText.length;
        const endHighlightIndex = searchText.length + ghostText.length;

        if (this.isGhostTextVisible) {
            this.searchInput.setSelectionRange(startHighlightIndex, endHighlightIndex);
        }
    }

    componentDidMount() {
        initializeLazyScenarioStore(this.props.scenarioId, this.props.isUsing3S);
    }

    render() {
        const {
            scenarioId,
            searchPlaceHolderText,
            isDisabled,
            isSuggestionsCalloutVisible,
        } = this.props;

        const searchInputClasses = classNames(
            styles.searchBoxInput,
            isDisabled && styles.isDisabled,
            isSearchSessionActive(scenarioId)
                ? styles.inSearchinputPlaceholder
                : styles.inputPlaceholder
        );

        const { ariaLabel, placeholder } = getPlaceholderAndAriaLabel(
            scenarioId,
            searchPlaceHolderText
        );

        return (
            <div className={styles.searchBoxInputContainer}>
                <input
                    aria-activedescendant={this.activeDescendant}
                    aria-autocomplete="list"
                    aria-label={ariaLabel}
                    aria-owns={isSuggestionsCalloutVisible ? SUGGESTIONS_CALLOUT_ID : ''}
                    autoComplete="off"
                    className={searchInputClasses}
                    disabled={isDisabled}
                    onChange={this.onSearchTextChange}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    placeholder={placeholder}
                    ref={ref => (this.searchInput = ref)}
                    value={this.searchInputText}
                    spellCheck={false}
                    autoCorrect="off"
                />
            </div>
        );
    }

    /**
     * Key handler for special key input into the input element.
     */
    private onKeyDown = async (evt: React.KeyboardEvent<unknown>) => {
        evt.stopPropagation();
        const evtKeyCode = evt.keyCode;

        const scenarioId = this.props.scenarioId;
        const {
            ghostText,
            selectedSuggestionIndex,
            isSuggestionsDropdownVisible,
        } = getScenarioStore(scenarioId);

        onKeyDownSearchInput(evtKeyCode, scenarioId);

        /**
         * This handles a very specific scenario in which the ghost text matches
         * the key pressed. In this case, the key pressed is already part of the
         * input value, so this key down doesn't get handled by the input element's
         * onChange handler (since the input value didn't change). If this
         * scenario is detected, we want to ensure the onChange handler is called.
         */
        if (evt.key === ghostText) {
            this.onSearchTextChange();
        }

        switch (evtKeyCode) {
            case KeyboardCharCodes.Enter: {
                // Clear selection (non-default behavior of IE/Edge).
                this.clearSelection();

                onEnterPressedSearchInput(
                    selectedSuggestionIndex,
                    this.searchInput.value,
                    this.isGhostTextVisible,
                    scenarioId
                );
                break;
            }
            case KeyboardCharCodes.Backspace: {
                onBackspacePressedSearchInput(scenarioId, this.getCursorPosition());
                break;
            }
            case KeyboardCharCodes.Up_arrow:
                // Prevents cursor from going to beginning of input.
                evt.preventDefault();

                onUpArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Down_arrow:
                onDownArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Right_arrow: {
                this.acceptGhostTextHandler({ isBack: false });
                break;
            }
            case KeyboardCharCodes.Left_arrow: {
                const ghostTextAccepted = this.acceptGhostTextHandler({ isBack: true });

                /**
                 * If no action was taken with respect to ghost text, then dispatch
                 * action to handle left arrow press.
                 */
                if (!ghostTextAccepted) {
                    onLeftArrowPressedSearchInput(this.getCursorPosition(), scenarioId);
                }
                break;
            }
            case KeyboardCharCodes.Tab: {
                if (this.isGhostTextVisible) {
                    // Prevent browser from shifting focus.
                    evt.preventDefault();

                    // Update search text.
                    this.acceptGhostTextHandler({ isBack: false });

                    // Clear selection (non-default behavior of browser).
                    this.clearSelection();
                }
                break;
            }
            case KeyboardCharCodes.Esc: {
                // Prevents IE/Edge behavior of putting last text back into the input.
                evt.preventDefault();

                onEscapePressedSearchInput(scenarioId, isSuggestionsDropdownVisible);
                break;
            }
            default:
                break;
        }
    };

    /**
     * Click handler when user clicks in input.
     */
    private onClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        onSearchInputClicked(this.props.scenarioId);
    };

    /**
     * Focus handler for when the SearchInput component receives focus.
     */
    private onFocus = () => {
        onSearchInputFocused(this.props.scenarioId, this.props.isUsing3S);
    };

    /**
     * Need to do this synchronously or there'll be rendering issues and the value
     * of the input element will be empty.
     */
    private onSearchTextChange = () => {
        const scenarioId = this.props.scenarioId;
        const searchText = this.searchInput.value;

        onSearchTextChanged(searchText, scenarioId, true /* showSuggestionsUi */);
        getSuggestions(scenarioId);
    };

    /**
     * Gets cursor position if no text is selected. If text is selected, then
     * -1 is returned.
     */
    private getCursorPosition() {
        /**
         * If reference to input is null (component unmounted), return -1
         * indicating that cursor position shouldn't be used.
         */
        if (!this.searchInput) {
            return -1;
        }

        const isTextSelected = this.searchInput.selectionStart !== this.searchInput.selectionEnd;
        return isTextSelected ? -1 : this.searchInput.selectionStart;
    }

    /**
     * Handler to determine how we should incorporate the suggestion up for
     * autocompletion.
     *
     * @param isBack A signal that the trigger is a "back" trigger (i.e. left arrow).
     * @returns True if the search box needed to be updated to indicate
     * work was done. Otherwise, returns false.
     */
    private acceptGhostTextHandler(params: { isBack: boolean }): boolean {
        const { isBack } = params;
        const scenarioId = this.props.scenarioId;

        // If there's no text to autocomplete, then return.
        if (!this.isGhostTextVisible) {
            return false;
        }

        // If there's no suggestion, there's nothing to complete.
        const suggestion = getSuggestionAtIndex(0 /* index */, scenarioId);
        if (!suggestion) {
            return false;
        }

        /**
         * If the accept trigger is "forward" (i.e. right arrow or Tab), and the
         * suggestion being autocompleted is a pill (people suggestions for now),
         * then overall handling will be different.
         */
        const shouldCompletePill = !isBack && suggestion.kind === SuggestionKind.People;

        /**
         * If we're completing a pill suggestion, then clear input text and
         * add the pill to the search box. Otherwise, just complete the text.
         */
        if (shouldCompletePill) {
            // Add pill to search box.
            onPillSuggestionSelected(
                suggestion as PeopleSuggestion,
                true /* suggestionSelected */,
                scenarioId
            );

            // Clear search box text and fetch new suggestions.
            onSearchTextChanged('', scenarioId, true /* showSuggestionsUi */);
            getSuggestions(scenarioId);
        } else {
            // Call input's onChange handler.
            this.onSearchTextChange();
        }

        // Dispatch action to instrument input changing because of ghost text.
        onGhostTextAccepted(scenarioId);

        return true;
    }

    /**
     * Clears selected text in the input.
     */
    private clearSelection() {
        const searchInputEnd = this.searchInput.value.length;
        this.searchInput.setSelectionRange(searchInputEnd, searchInputEnd);
    }
}
